import React, { useState } from 'react';
import { X, FileText, Calendar, Users, Clock, DollarSign, BarChart3 } from 'lucide-react';
import { api } from '../auth/authStore';
import toast from 'react-hot-toast';

interface ReportGenerationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onReportGenerated: () => void;
}

interface ReportFormData {
  reportType: string;
  name: string;
  description: string;
  format: string;
  filters: {
    startDate: string;
    endDate: string;
    clientIds: string[];
    userIds: string[];
    taskTypes: string[];
    departments: string[];
  };
}

export const ReportGenerationModal: React.FC<ReportGenerationModalProps> = ({
  isOpen,
  onClose,
  onReportGenerated
}) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<ReportFormData>({
    reportType: '',
    name: '',
    description: '',
    format: 'pdf',
    filters: {
      startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
      endDate: new Date().toISOString().split('T')[0],
      clientIds: [],
      userIds: [],
      taskTypes: [],
      departments: []
    }
  });

  const reportTypes = [
    {
      value: 'financial',
      label: 'Financial Report',
      description: 'Revenue, expenses, and financial performance metrics',
      icon: DollarSign,
      color: 'text-green-600'
    },
    {
      value: 'time-tracking',
      label: 'Time Tracking Report',
      description: 'Billable hours, time utilization, and productivity metrics',
      icon: Clock,
      color: 'text-blue-600'
    },
    {
      value: 'attendance',
      label: 'Attendance Report',
      description: 'Staff attendance patterns, working hours, and leave analysis',
      icon: Users,
      color: 'text-purple-600'
    },
    {
      value: 'client-activity',
      label: 'Client Activity Report',
      description: 'Client engagement, project status, and relationship metrics',
      icon: BarChart3,
      color: 'text-orange-600'
    },
    {
      value: 'task-performance',
      label: 'Task Performance Report',
      description: 'Task completion rates, efficiency, and performance analysis',
      icon: FileText,
      color: 'text-indigo-600'
    },
    {
      value: 'comprehensive',
      label: 'Comprehensive Report',
      description: 'All-in-one report with financial, operational, and performance data',
      icon: BarChart3,
      color: 'text-red-600'
    }
  ];

  const formats = [
    { value: 'pdf', label: 'PDF Document', description: 'Professional formatted document' },
    { value: 'csv', label: 'CSV Spreadsheet', description: 'Data in comma-separated format' },
    { value: 'excel', label: 'Excel Workbook', description: 'Microsoft Excel format with charts' },
    { value: 'json', label: 'JSON Data', description: 'Raw data in JSON format' }
  ];

  const handleInputChange = (field: keyof ReportFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFilterChange = (field: keyof ReportFormData['filters'], value: any) => {
    setFormData(prev => ({
      ...prev,
      filters: { ...prev.filters, [field]: value }
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.reportType) {
      toast.error('Please select a report type');
      return;
    }

    setLoading(true);

    try {
      console.log('Generating report with data:', formData);

      const response = await api.post('/firm-admin/reports/generate', formData);

      if (response.data.success) {
        toast.success('Report generation started successfully');
        onReportGenerated();
        onClose();

        // Reset form
        setFormData({
          reportType: '',
          name: '',
          description: '',
          format: 'pdf',
          filters: {
            startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
            endDate: new Date().toISOString().split('T')[0],
            clientIds: [],
            userIds: [],
            taskTypes: [],
            departments: []
          }
        });
      } else {
        toast.error(response.data.message || 'Failed to generate report');
      }
    } catch (error: any) {
      console.error('Error generating report:', error);
      const errorMessage = error.response?.data?.error?.message ||
                          error.response?.data?.message ||
                          error.message ||
                          'Failed to generate report';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const generateReportName = (type: string): string => {
    const reportType = reportTypes.find(rt => rt.value === type);
    const date = new Date().toLocaleDateString();
    return `${reportType?.label || 'Report'} - ${date}`;
  };

  const handleReportTypeChange = (type: string) => {
    handleInputChange('reportType', type);
    if (!formData.name) {
      handleInputChange('name', generateReportName(type));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Generate New Report</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          {/* Report Type Selection */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Select Report Type *
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {reportTypes.map((type) => {
                const Icon = type.icon;
                return (
                  <div
                    key={type.value}
                    className={`relative border-2 rounded-lg p-4 cursor-pointer transition-all ${
                      formData.reportType === type.value
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => handleReportTypeChange(type.value)}
                  >
                    <div className="flex items-start">
                      <Icon className={`w-6 h-6 ${type.color} mr-3 mt-1`} />
                      <div>
                        <h3 className="text-sm font-medium text-gray-900">{type.label}</h3>
                        <p className="text-xs text-gray-500 mt-1">{type.description}</p>
                      </div>
                    </div>
                    {formData.reportType === type.value && (
                      <div className="absolute top-2 right-2">
                        <div className="w-4 h-4 bg-primary-500 rounded-full flex items-center justify-center">
                          <div className="w-2 h-2 bg-white rounded-full" />
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Report Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Report Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="Enter report name"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Output Format
              </label>
              <select
                value={formData.format}
                onChange={(e) => handleInputChange('format', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                {formats.map(format => (
                  <option key={format.value} value={format.value}>
                    {format.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Optional description for this report"
            />
          </div>

          {/* Date Range */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Start Date
              </label>
              <input
                type="date"
                value={formData.filters.startDate}
                onChange={(e) => handleFilterChange('startDate', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                End Date
              </label>
              <input
                type="date"
                value={formData.filters.endDate}
                onChange={(e) => handleFilterChange('endDate', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-end space-x-4 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !formData.reportType}
              className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Generating...
                </>
              ) : (
                <>
                  <FileText className="w-4 h-4 mr-2" />
                  Generate Report
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
